import { ErrorFallback } from "@base/ui/ErrorFallback";
import { LoadingFallback } from "@base/ui/LoadingFallback";
import type { ReactNode } from "react";
import { Suspense } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

export interface AsyncBoundaryProps {
  children: ReactNode;
  /** 데이터 로딩 중일 때 보여줄 Suspense fallback. 기본값: <LoadingFallback /> */
  loadingFallback?: ReactNode;
  /** 에러 시 보여줄 ErrorBoundary FallbackComponent (error, resetErrorBoundary 전달). 기본값: ErrorFallback */
  FallbackComponent?: React.ComponentType<FallbackProps>;
  /** 값이 바뀌면 에러 바운더리가 리셋됨. TanStack Query queryKey 배열을 넣으면 해당 쿼리 키가 바뀔 때(예: 라우트/리소스 변경) boundary가 리셋됨. 같은 키로 refetch만 할 때는 "다시 시도" 버튼에서 resetErrorBoundary() 호출 권장. */
  resetKeys?: unknown[];
}

/**
 * 자식 컴포넌트를 ErrorBoundary + Suspense로 감싸서 비동기/서스펜스 모드로 동작하게 함.
 * useQuery(..., { suspense: true }) 등과 함께 사용 시:
 * - 로딩 중 → Suspense가 loadingFallback 표시
 * - 에러 → ErrorBoundary가 FallbackComponent 표시 (기본: 재시도 버튼 있는 ErrorFallback)
 * - 성공 → children 렌더링
 *
 * @example
 * <AsyncBoundary>
 *   <PageThatUsesSuspenseQueries />
 * </AsyncBoundary>
 */
export function AsyncBoundary({ children, loadingFallback, FallbackComponent, resetKeys }: AsyncBoundaryProps) {
  const fallback = loadingFallback ?? <LoadingFallback />;

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent ?? ErrorFallback} resetKeys={resetKeys}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}
